// /admin/v1/tasks/* — 跨用户生成任务列表 + 详情 + 操作 (软删/取消/重试)。
// 覆盖写真套系 (package_id) 与创作工具 (workflow_code=image_edit/beauty) — 同一张表。

import { get, list, del, post } from './client';
import type { ListResult } from './client';

export type TaskStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';

// 任务结果图项 (后端 outputs JSONB 数组元素, 读时已签名)。
export interface TaskOutput {
  url?: string;
  thumb_url?: string;
  medium_url?: string;
  width?: number;
  height?: number;
  seed?: number;
  provider?: string;
  node_id?: string;
  [k: string]: unknown;
}

// toOutputImages 归一 outputs 形态 → TaskOutput[]。后端 image_edit/beauty/写真 的
// outputs 是对象 {images:[{url}], image_urls:[...]}; 部分工作流可能是数组。
export function toOutputImages(
  outputs?: TaskOutput[] | Record<string, unknown> | null
): TaskOutput[] {
  if (!outputs) return [];
  if (Array.isArray(outputs)) return outputs as TaskOutput[];
  const obj = outputs as Record<string, unknown>;
  if (Array.isArray(obj.images)) return obj.images as TaskOutput[];
  if (Array.isArray(obj.image_urls)) {
    return (obj.image_urls as string[]).map((u) => ({ url: u }));
  }
  return [];
}

// 源图项 (GetTask 才返回): 后端把 input 里的 OSS key 现签成三档 read URL。
export interface SourceImage {
  url: string;
  thumb_url?: string;
  medium_url?: string;
}

export interface Task {
  id: string;
  user_id: string;
  app_id: number;
  workflow_code: string;
  package_id?: number;
  // package_name: 按 package_id 查 content_packages.name (无套系则空)。
  package_name?: string;
  workflow_id?: string;
  status: TaskStatus;
  // source_images: 源图现签 (创作工具 source_image_url / 写真 face_image_urls)。
  // 仅 getTask 详情返回; 列表不含。
  source_images?: SourceImage[];
  // input: 校验后的答题 / prompt / 脸图 URL / 套系参数 (JSONB)。
  input?: unknown;
  // outputs: 结果 (JSONB)。形态因工作流而异: image_edit/beauty/写真 是对象
  // {images:[{url,...}], image_urls:[...], kind, action_type}; 部分工作流是数组。
  // 用 toOutputImages() 归一成图数组, 不要直接当数组迭代。
  outputs?: TaskOutput[] | Record<string, unknown> | null;
  error_code?: string;
  error_msg?: string;
  provider_chain_used?: unknown;
  cost_cny?: string;
  duration_ms?: number;
  priority?: number;
  started_at?: string | null;
  finished_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ListTasksQuery {
  app_id?: number;
  workflow_code?: string;
  status?: TaskStatus;
  user_id?: string;
  // Cursor pagination — server returns next cursor in envelope.cursor.next.
  cursor?: string;
  limit?: number;
}

export function listTasks(q: ListTasksQuery = {}): Promise<ListResult<Task>> {
  return list<Task>('/admin/v1/tasks', q);
}

export function getTask(id: string): Promise<Task> {
  return get<Task>(`/admin/v1/tasks/${id}`);
}

// deleteTask 软删任务 (set deleted_at)。任意状态可删。返回 204 (无 body)。
export function deleteTask(id: string): Promise<void> {
  return del(`/admin/v1/tasks/${id}`);
}

// cancelTask 取消 in-flight 任务 (queued/running/warming): 尽力 terminate workflow +
// 置 cancelled + 幂等退还已扣钻石。返回更新后的任务。
export function cancelTask(id: string): Promise<Task> {
  return post<Task>(`/admin/v1/tasks/${id}/cancel`);
}

// retryTask admin 重跑任务 (终态 failed/done/cancelled 均可): 不向用户扣费, 复制原 input 建新任务。
// 返回新任务 (含新 id)。
export function retryTask(id: string): Promise<Task> {
  return post<Task>(`/admin/v1/tasks/${id}/retry`);
}
